import PropTypes from 'prop-types';
import React from 'react';

import SegmentAllocator from './SegmentAllocator';

/**
 * Notify Angular of any changes to allocation counts to trigger
 * showing/hiding the "tag" icon for a data entry
 * @param {Object} dataEntrySegmentDetails
 * @param {function(): void} onFirstAllocationCreated
 * @param {function(): void} onAllocationsRemoved
 */
export const notifyAllocationChange = (
  dataEntrySegmentDetails,
  onFirstAllocationCreated,
  onAllocationsRemoved,
) => {
  if (dataEntrySegmentDetails && dataEntrySegmentDetails.categories.length) {
    onFirstAllocationCreated();
  } else {
    onAllocationsRemoved();
  }
};

/**
 * Search transaction data entries for all that belong to the same ledger.
 * @param {array} dataEntries
 * @param {string} ledgerAccountId
 * @return {array} dataEntry ids
 */
const getDataEntriesInSameLedger = (dataEntries, ledgerAccountId) =>
  dataEntries.filter(entry => entry.ledgerAccount.id === ledgerAccountId);

/**
 * Get total amount in cents of data entries.
 * @param {array} dataEntries
 * @return {number} total in cents
 */
const getTotalDataEntriesAmount = dataEntries =>
  dataEntries.reduce((total, de) => total + de.amountInCents, 0);

/**
 * @param {Object.<string, any>} props Component props
 * @param {string} props.clientId
 * @param {array} props.dataEntries - all data entries belonging to current ledger
 * @param {string} props.ledgerAccountId - id of current ledger
 * @param {string} props.ledgerName - name of current ledger
 * @param {function(): void} props.onFirstAllocationCreated - function to call when the first
 * allocation is created. This is used to tell angular to show a "tag" icon beside the data entry
 * @param {function(): void} props.onAllocationsRemoved - function to call when all allocations
 * are removed. This is used to tell angular to remove the "tag" icon beside the data entry
 * @param {array} segmentCategories
 * @param {string} transactionId
 * @return {ReactNode}
 * @constructor
 */
export default function SegmentAllocatorContainer({
  clientId,
  dataEntries,
  ledgerAccountId,
  ledgerName,
  onAllocationsRemoved,
  onFirstAllocationCreated,
  segmentCategories,
  transactionId,
}) {
  // Search for data entries that share the same ledger for bulk mutation
  const dataEntryFromSameLedger = getDataEntriesInSameLedger(dataEntries, ledgerAccountId);

  const totalLedgerAmount = getTotalDataEntriesAmount(dataEntryFromSameLedger);

  const dataEntrySegmentDetails = {
    dataEntryId: dataEntries[0].id,
    categories:
      dataEntryFromSameLedger.length && dataEntryFromSameLedger[0].segmentAllocations
        ? [Object.values(dataEntryFromSameLedger[0].segmentAllocations)]
        : [],
  };

  notifyAllocationChange(dataEntrySegmentDetails, onFirstAllocationCreated, onAllocationsRemoved);

  return (
    <SegmentAllocator
      clientId={clientId}
      clientSegmentCategories={segmentCategories}
      dataEntryIds={dataEntryFromSameLedger.map(de => de.id)}
      dataEntrySegmentDetails={dataEntrySegmentDetails}
      ledgerAccountId={ledgerAccountId}
      ledgerName={ledgerName}
      totalLedgerAmount={totalLedgerAmount}
      transactionId={transactionId}
    />
  );
}

const DataEntry = {
  id: PropTypes.string,
  amount: PropTypes.number,
  entryType: PropTypes.string,
  ledgerAccountId: PropTypes.string,
  ledgerName: PropTypes.string,
};

SegmentAllocatorContainer.propTypes = {
  clientId: PropTypes.string.isRequired,
  dataEntries: PropTypes.arrayOf(PropTypes.shape(DataEntry)).isRequired,
  ledgerAccountId: PropTypes.string.isRequired,
  ledgerName: PropTypes.string.isRequired,
  onFirstAllocationCreated: PropTypes.func.isRequired,
  onAllocationsRemoved: PropTypes.func.isRequired,
  segmentCategories: PropTypes.arrayOf(PropTypes.object).isRequired,
  transactionId: PropTypes.string.isRequired,
};
